import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useLocale } from '../../context/LocaleContext';
import api from '../../services/api';
import { toast } from 'react-toastify';

const Profile = () => {
  const { user, updateUser } = useAuth();
  const { t } = useLocale();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    mobile: '',
    current_password: '',
    new_password: '',
    new_password_confirmation: ''
  });

  useEffect(() => {
    if (user) {
      setFormData((current) => ({
        ...current,
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        mobile: user.mobile || user.phone || '',
      }));
    }
  }, [user]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.put('/user/profile', {
        first_name: formData.first_name,
        last_name: formData.last_name,
        mobile: formData.mobile
      });

      const updated = response.data?.data || response.data?.user;
      if (updateUser && updated) {
        updateUser(updated);
      }
      toast.success('Profile updated successfully!');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();

    if (formData.new_password !== formData.new_password_confirmation) {
      toast.error('New passwords do not match');
      return;
    }

    setLoading(true);

    try {
      await api.put('/user/password', {
        current_password: formData.current_password,
        new_password: formData.new_password,
        new_password_confirmation: formData.new_password_confirmation
      });

      toast.success('Password updated successfully!');
      setFormData({
        ...formData,
        current_password: '',
        new_password: '',
        new_password_confirmation: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-medium text-gray-900">{t('myProfile')}</h1>
        <p className="mt-2 text-sm text-gray-500">{t('profileLead')}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="auth-card px-7 py-8">
          <h2 className="text-lg font-medium text-gray-900 mb-6">{t('profileInfo')}</h2>
          <form onSubmit={handleUpdateProfile} className="space-y-5">
            <div>
              <label htmlFor="first_name" className="block text-sm font-medium text-gray-700 mb-2">{t('firstName')}</label>
              <input
                id="first_name"
                type="text"
                name="first_name"
                value={formData.first_name}
                onChange={handleChange}
                className="auth-input auth-input-plain"
                required
              />
            </div>

            <div>
              <label htmlFor="last_name" className="block text-sm font-medium text-gray-700 mb-2">{t('lastName')}</label>
              <input
                id="last_name"
                type="text"
                name="last_name"
                value={formData.last_name}
                onChange={handleChange}
                className="auth-input auth-input-plain"
                required
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">{t('email')}</label>
              <input
                id="email"
                type="email"
                value={formData.email}
                className="auth-input auth-input-plain bg-[#f4f4f5] text-gray-500"
                disabled
              />
              <p className="text-xs text-gray-400 mt-2">{t('emailLocked')}</p>
            </div>

            <div>
              <label htmlFor="mobile" className="block text-sm font-medium text-gray-700 mb-2">{t('mobile')}</label>
              <input
                id="mobile"
                type="tel"
                name="mobile"
                value={formData.mobile}
                onChange={handleChange}
                placeholder="+974xxxxxxxx"
                className="auth-input auth-input-plain"
              />
            </div>

            <button type="submit" disabled={loading} className="auth-submit">
              {loading ? t('updating') : t('updateProfile')}
            </button>
          </form>
        </div>

        <div className="auth-card px-7 py-8">
          <h2 className="text-lg font-medium text-gray-900 mb-6">{t('changePassword')}</h2>
          <form onSubmit={handleUpdatePassword} className="space-y-5">
            <div>
              <label htmlFor="current_password" className="block text-sm font-medium text-gray-700 mb-2">{t('currentPassword')}</label>
              <input
                id="current_password"
                type="password"
                name="current_password"
                value={formData.current_password}
                onChange={handleChange}
                autoComplete="current-password"
                className="auth-input auth-input-plain"
                required
              />
            </div>

            <div>
              <label htmlFor="new_password" className="block text-sm font-medium text-gray-700 mb-2">{t('newPassword')}</label>
              <input
                id="new_password"
                type="password"
                name="new_password"
                value={formData.new_password}
                onChange={handleChange}
                autoComplete="new-password"
                className="auth-input auth-input-plain"
                required
                minLength="6"
              />
            </div>

            <div>
              <label htmlFor="new_password_confirmation" className="block text-sm font-medium text-gray-700 mb-2">{t('confirmNew')}</label>
              <input
                id="new_password_confirmation"
                type="password"
                name="new_password_confirmation"
                value={formData.new_password_confirmation}
                onChange={handleChange}
                autoComplete="new-password"
                className="auth-input auth-input-plain"
                required
                minLength="6"
              />
            </div>

            <button type="submit" disabled={loading} className="auth-submit">
              {loading ? t('updating') : t('updatePassword')}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Profile;
